<template>
  <div
    name="rb-input"
    :style="cssVars"
  >
    <label :class="{ primary, secondary }">{{ props.label + (props.required ? ' *' : '') }}</label>
    <input
      :type="props.type"
      :label="props.label"
      :placeholder="props.placeholder"
      :required="props.required"
      :class="{ primary, secondary }"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :pattern="props.pattern"
      :maxLength="props.maxLength"
      :minLength="props.minLength"
      :inputmode="props.inputmode"
      v-model="input"
      @change="emits('update:modelValue', input)"
      @keyup="emits('keyup', $event)"
      @focusout="emits('focusout', $event)"
      @focusin="emits('focusin', $event)"
    >
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults, ref, watchEffect } from 'vue';

const emits = defineEmits(['update:modelValue', 'keyup', 'focusout', 'focusin']);
const props = withDefaults(defineProps<{
  modelValue: any,
  label: string,
  color?: string,
  placeholder?: string,
  type?: string,
  required?: boolean,
  primary?: boolean,
  secondary?: boolean,
  pattern?: string,
  minLength?: number,
  maxLength?: number,
  inputmode?: any,
  disabled?: boolean,
  readonly?: boolean,
}>(), {
  placeholder: '',
  color: 'rgba(0, 0, 0, 0.6)',
  type: 'text',
  pattern: undefined,
  minLength: undefined,
  maxLength: 255,
  inputmode: undefined,
  required: false,
  readonly: false
});

const input = ref('');
watchEffect(() => {
  input.value = props.modelValue;
});

const cssVars = computed(() => {
  return {
    ['--color']: props.color
  };
});
</script>

<style lang="scss" scoped>
div[name="rb-input"] {
  box-sizing: border-box;
  height: 56px;
  width: 100%;
  position: relative;

  &>input {
    transition: 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    padding: 27px 16px 5px;

    outline: none;
    border: none;
    border-bottom: 1px solid var(--border);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;

    font-size: 1rem;
    line-height: 100%;

    background-color: var(--input);

    &::placeholder {
      visibility: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:focus {
      background-color: var(--input-focus);
      border-bottom: 1px solid var(--color);
      box-shadow: 0px 1px 0px 0px var(--color);

      &.primary {
        border-bottom: 1px solid var(--primary);
        box-shadow: 0px 1px 0px 0px var(--primary);
      }

      &.secondary {
        border-bottom: 1px solid var(--secondary);
        box-shadow: 0px 1px 0px 0px var(--secondary);
      }

      &::placeholder {
        visibility: unset;
      }
    }
  }

  &>label {
    pointer-events: none;
    position: absolute;
    top: calc(50% - 0.5rem);
    left: 0.75rem;
    color: var(--text);
    font-size: 1rem;
    font-weight: normal;
    line-height: 1rem;
    letter-spacing: 0;
    transition: 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:has(input:disabled) {
    opacity: 0.6;
  }

  /** input with focus */
  /** input placeholder not visible and input not date type */
  &:has(input:focus),
  &:has(input:not(:placeholder-shown)) {
    &>label {
      top: 7px;
      font-size: 0.75em;
    }
  }

  &:has(input:focus) {
    &>label {
      color: var(--color);

      &.primary {
        color: var(--primary) !important;
      }

      &.secondary {
        color: var(--secondary) !important;
      }
    }
  }
}
</style>
